import { LoaderFunctionArgs, redirect } from 'react-router';

import { clearExternalInvitations } from '@/domain/external-invitation/actions/clear-extenal-invitations.action';
import { getExternalInvitations } from '@/domain/external-invitation/actions/get-external-invitations.action';
import { Sector, SectorValues } from '@/domain/impact/impact.entity';
import { runImpactCalculateCo2ForProject } from '@/domain/impact-calculate/actions/run-impact-calculate-co2-for-project.action';
import { getAllImpactCalculateForAllProjects } from '@/domain/impact-calculate-festival/actions/get-all-impact-calculate-festival-for-all-projects.action';
import { runImpactCalculateFestivalCo2ForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-co2-for-project.action';
import { runImpactCalculateFestivalEnvForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-env-for-project.action';
import { runImpactCalculateFestivalEssForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-ess-for-project.action';
import { clearImpactCo2Answers } from '@/domain/impact-co2/actions/clear-impact-co2-answers.action';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { getImpactEnvQuestions } from '@/domain/impact-env/actions/get-impact-env-questions.action';
import { getImpactEnvResponses } from '@/domain/impact-env/actions/get-impact-env-responses.action';
import { getImpactEssQuestions } from '@/domain/impact-ess/actions/get-impact-ess-questions.action';
import { getImpactEssResponses } from '@/domain/impact-ess/actions/get-impact-ess-responses.action';
import { getImpactReferences } from '@/domain/impact-references/actions/get-impact-references.action';
import { getImpactStatsPublic } from '@/domain/impact-stats/actions/get-impact-stats-public.action';
import { projectSlice } from '@/domain/project/project.slice';
import { clearCurrentProjectFestival } from '@/domain/project-festival/actions/clear-current-project-festival.action';
import { getProjectFestival } from '@/domain/project-festival/actions/get-project-festival.action';
import { getProjectsFestival } from '@/domain/project-festival/actions/get-projects-festival.action';
import { setProjectFestivalCurrentSector } from '@/domain/project-festival/actions/set-project-festival-current-sector-type.action';
import { store } from '@/domain/store';
import { prepareAuth } from '@/ui/navigation/loaders/prepare-auth-loader';

import { makeCommonGets } from '../appLoaders';

export async function festivalIndexLoader() {
  await store.dispatch(projectSlice.actions.changeMode('festival'));
  return null;
}

export async function projectsLoader() {
  const { userIsConnected } = await prepareAuth();
  if (!userIsConnected) {
    return null;
  }
  makeCommonGets();
  await store.dispatch(getProjectsFestival());
  store.dispatch(getAllImpactCalculateForAllProjects());
  store.dispatch(clearCurrentProjectFestival());
  store.dispatch(clearExternalInvitations());
  return null;
}

export async function projectLoader({ params }: LoaderFunctionArgs) {
  const { userIsConnected } = await prepareAuth();
  if (!userIsConnected) {
    return null;
  }
  makeCommonGets();
  const projectId = parseInt(params.id as string);
  await store.dispatch(getProjectFestival(projectId));
  await store.dispatch(getExternalInvitations({ projectId }));
  store.dispatch(clearImpactCo2Answers());
  store.dispatch(getImpactReferences({ projectId }));
  store.dispatch(runImpactCalculateFestivalCo2ForProject({ projectId }));
  store.dispatch(runImpactCalculateFestivalEnvForProject({ projectId }));
  store.dispatch(runImpactCalculateFestivalEssForProject({ projectId }));
  store.dispatch(getImpactStatsPublic({ projectId }));
  store.dispatch(getImpactCo2Answers({ projectId }));
  store.dispatch(getImpactEssQuestions({}));
  store.dispatch(getImpactEnvQuestions());
  store.dispatch(getImpactEssResponses({ projectId }));
  store.dispatch(getImpactEnvResponses({ projectId }));
  return null;
}

export async function editProjectLoader({ params }: LoaderFunctionArgs) {
  const { userIsConnected } = await prepareAuth();
  if (!userIsConnected) {
    return null;
  }
  makeCommonGets();
  const projectId = parseInt(params.id as string);
  await store.dispatch(getProjectFestival(projectId));
  return null;
}

export async function sectorLoader({ params, request }: LoaderFunctionArgs) {
  const { userIsConnected } = await prepareAuth();
  if (!userIsConnected) {
    return null;
  }

  const projectId = parseInt(params.id as string);
  const url = new URL(request.url);
  const typeParam = url.searchParams.get('type');
  const sector = convertTypeParamToSector(typeParam);

  if (sector === null) {
    return redirect(projectId ? `/app/project/${projectId}` : 'app/projects');
  } else {
    try {
      store.dispatch(setProjectFestivalCurrentSector(sector));
    } catch {
      throw new Error('');
    }
  }

  makeCommonGets();
  await store.dispatch(getProjectFestival(projectId));
  await store.dispatch(getExternalInvitations({ projectId }));
  store.dispatch(clearImpactCo2Answers());
  store.dispatch(getImpactCo2Answers({ projectId }));
  store.dispatch(getImpactEssQuestions({}));
  store.dispatch(getImpactEnvQuestions());
  store.dispatch(getImpactEssResponses({ projectId }));
  store.dispatch(getImpactEnvResponses({ projectId }));
  store.dispatch(runImpactCalculateCo2ForProject({ projectId }));
  store.dispatch(runImpactCalculateFestivalEnvForProject({ projectId }));
  store.dispatch(runImpactCalculateFestivalEssForProject({ projectId }));
  store.dispatch(getImpactStatsPublic({ projectId }));
  return null;
}

function convertTypeParamToSector(type: string | null): Sector | null {
  if (SectorValues.includes(type as Sector)) {
    return type as Sector;
  } else {
    return null;
  }
}
